import React from "react";
import { motion } from "framer-motion";
import { ContainerScroll } from "../animations/ContainerScroll";
import { credentialsData } from "../../data/siteData";
import { Heart, Sparkles, Shield, Award } from "lucide-react";

export const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const badgeVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const values = [
    { icon: Heart, text: "Compassionate Care" },
    { icon: Shield, text: "Safe Space" },
    { icon: Sparkles, text: "Evidence-Based" },
    { icon: Award, text: "Professional Excellence" }
  ];

  return (
    <section
      id="about"
      className="relative bg-warm-cream z-20"
      data-testid="about-section"
    >
      <ContainerScroll
        titleComponent={
          <div className="mb-8">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-warm-sage uppercase tracking-[0.2em] text-xs font-bold mb-4"
            >
              Get to Know Me
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-warm-brown mb-4"
            >
              Meet Priya Ahuja
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-sans text-lg text-warm-brown/70"
            >
              Your partner in mental wellness
            </motion.p>
          </div>
        }
      >
        <div className="h-full flex flex-col items-center justify-center gap-8 p-4 md:p-8">
          {/* Values Grid - Replacing the PA circle */}
          <div className="grid grid-cols-2 gap-4 w-full max-w-md mb-6">
            {values.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="flex flex-col items-center p-4 bg-warm-bg rounded-2xl border border-warm-rose/20"
              >
                <item.icon className="w-8 h-8 text-warm-rose mb-2" />
                <span className="text-sm text-warm-brown font-medium text-center">{item.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Content */}
          <div className="w-full max-w-2xl text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-serif text-2xl sm:text-3xl text-warm-brown mb-2"
            >
              Priya Ahuja
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-warm-sage font-semibold mb-4"
            >
              Licensed Clinical Psychologist
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-sans text-warm-brown/80 leading-relaxed mb-6 text-sm sm:text-base"
            >
              With years of dedicated experience in clinical psychology, Priya Ahuja 
              provides a safe, compassionate, and non-judgmental space for individuals 
              navigating life's challenges. Her approach blends evidence-based therapies 
              with deep empathy, helping clients rediscover their inner strength. Based 
              in Mansarovar, Jaipur, she offers both in-person and online sessions.
            </motion.p>

            {/* Credentials */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-wrap justify-center gap-3"
            >
              {credentialsData.map((credential, index) => (
                <motion.span
                  key={index}
                  variants={badgeVariants}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="credential-badge inline-block px-4 py-2 bg-warm-sage/20 text-warm-brown rounded-full text-sm font-medium border border-warm-sage/30"
                  data-testid={`credential-badge-${index}`}
                >
                  {credential}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </div>
      </ContainerScroll>
    </section>
  );
};

export default AboutSection;
